import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ManageRentals.css';
import './RentEvidenceView.css';
import LandlordRentEvidencePanel from './RentEvidencePanel';
import AgentRentEvidencePanel from '../../Agent/components/RentEvidencePanel';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getMyProperties, getPropertyUnits } from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';

const HISTORY_KEY = 'tenantscore_rentEvidenceDetail';

function unitsFromApiResponse(res) {
  if (!res || res.status !== 'success' || !res.data) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d.units && Array.isArray(d.units)) return d.units;
  if (d.data && Array.isArray(d.data)) return d.data;
  return [];
}

/** Normalize agent assignments to the same shape as landlord my-properties rows for this view. */
function propertiesFromAgentAssignmentsResponse(res) {
  const d = res?.data;
  const list = Array.isArray(d?.content) ? d.content : Array.isArray(d) ? d : [];
  return list
    .filter((a) => a && a.propertyId)
    .map((a) => ({
      id: a.propertyId,
      name: a.propertyName || '',
      address: a.propertyAddress || '',
      location: a.location || undefined,
    }));
}

/**
 * Standalone dashboard section: pick a property, then review tenant rent evidence.
 * Used from landlord and agent main sidebars (not inside PropertyDetails).
 */
const RentEvidenceView = ({ isAgentView = false }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const selectedPropertyIdRef = useRef(null);
  selectedPropertyIdRef.current = selectedPropertyId;

  const filteredProperties = useMemo(() => {
    let filtered = properties;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((p) => {
        const nameMatch = p.name?.toLowerCase().includes(q);
        const addressMatch = p.address?.toLowerCase().includes(q);
        const agentMatch = p.assignedAgentName?.toLowerCase().includes(q);
        return nameMatch || addressMatch || agentMatch;
      });
    }
    return filtered;
  }, [properties, searchQuery]);

  useEffect(() => {
    if (isAgentView) return undefined;
    let cancelled = false;
    const fetchLandlordProperties = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyProperties({
          page: 0,
          size: 100,
          search: searchQuery,
        });
        if (cancelled) return;
        if (response.status === 'success' && response.data?.content) {
          setProperties(response.data.content);
        } else {
          setProperties([]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setErrorMessage(e.message || 'Failed to load properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLandlordProperties();
    return () => {
      cancelled = true;
    };
  }, [isAgentView, searchQuery]);

  useEffect(() => {
    if (!isAgentView) return undefined;
    let cancelled = false;
    const fetchAgentAssignments = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyAgentAssignments({ page: 0, size: 100 });
        if (cancelled) return;
        setProperties(propertiesFromAgentAssignmentsResponse(response));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setErrorMessage(e.message || 'Failed to load assigned properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAgentAssignments();
    return () => {
      cancelled = true;
    };
  }, [isAgentView]);

  useEffect(() => {
    if (!selectedPropertyId) {
      setUnits([]);
      return undefined;
    }
    let cancelled = false;
    const loadUnits = async () => {
      setUnitsLoading(true);
      try {
        const fromList = properties.find((p) => p.id === selectedPropertyId);
        const embedded = fromList?.units;
        if (Array.isArray(embedded) && embedded.length > 0) {
          if (!cancelled) setUnits(embedded);
          return;
        }
        const res = await getPropertyUnits(selectedPropertyId);
        if (!cancelled) setUnits(unitsFromApiResponse(res));
      } catch (e) {
        console.error(e);
        if (!cancelled) setUnits([]);
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    };
    loadUnits();
    return () => {
      cancelled = true;
    };
  }, [selectedPropertyId, properties]);

  const openProperty = (property) => {
    window.history.pushState(
      { [HISTORY_KEY]: true, propertyId: property.id },
      '',
      window.location.href
    );
    setSelectedPropertyId(property.id);
    setSelectedPropertyName(property.name || '');
  };

  const handleBackToProperties = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedPropertyId(null);
    setSelectedPropertyName('');
    setUnits([]);
  };

  /** One hardware / browser “back” from a property should return to the property list (pushState adds that step). */
  useEffect(() => {
    const onPopState = () => {
      if (selectedPropertyIdRef.current != null) {
        setSelectedPropertyId(null);
        setSelectedPropertyName('');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  if (selectedPropertyId) {
    return (
      <div className="manage-rentals-container rent-evidence-view">
        <CollapsibleTopPanel id="rent-evidence-detail-top-panel" defaultOpenOnMobile={false}>
          <div className="rent-evidence-view__back-row">
            <button
              type="button"
              className="rent-evidence-view__back-btn"
              onClick={handleBackToProperties}
              aria-label="Back to properties"
            >
              ← Back to properties
            </button>
          </div>
          <div className="rent-evidence-view__detail-header page-header">
            <div className="rent-evidence-view__title-wrap">
              <h1 className="page-title rent-evidence-view__title">Rent evidence</h1>
              {selectedPropertyName ? (
                <p className="rent-evidence-view__property-name">{selectedPropertyName}</p>
              ) : null}
            </div>
          </div>
        </CollapsibleTopPanel>
        {unitsLoading ? (
          <div className="empty-state">Loading property units…</div>
        ) : isAgentView ? (
          <AgentRentEvidencePanel propertyId={selectedPropertyId} units={units} />
        ) : (
          <LandlordRentEvidencePanel propertyId={selectedPropertyId} units={units} />
        )}
      </div>
    );
  }

  return (
    <div className="manage-rentals-container rent-evidence-view">
      <CollapsibleTopPanel id="rent-evidence-top-panel" defaultOpenOnMobile={false}>
        <div className="page-header">
          <h1 className="page-title">Rent evidence</h1>
          <div className="page-header-actions">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CollapsibleTopPanel>

      {errorMessage ? (
        <div
          className="error-message"
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px',
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      {loading ? (
        <div className="empty-state">Loading properties…</div>
      ) : filteredProperties.length === 0 ? (
        <div className="empty-state">
          {isAgentView
            ? 'No properties assigned to you yet.'
            : 'No properties yet. Add a property under Manage Rentals to collect rent evidence.'}
        </div>
      ) : (
        <div className="properties-grid-overview">
          {filteredProperties.map((property) => {
            const addrLine = property.address?.trim() || '';
            const cityLine = property.location?.city?.trim() || '';
            const primaryLocationLine = addrLine || cityLine;

            return (
              <button
                key={property.id}
                type="button"
                className="property-overview-card rent-evidence-view__card"
                onClick={() => openProperty(property)}
              >
                <div className="property-overview-header">
                  <h3>{property.name}</h3>
                  {primaryLocationLine ? (
                    <p className="property-overview-address">{primaryLocationLine}</p>
                  ) : null}
                </div>
                <span className="rent-evidence-view__card-cta">View submissions →</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RentEvidenceView;
